import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import CheckboxField from "../../components/Editing/CheckboxField";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedDriveTest: Api.DriveTest;
  storedDriveTest: Api.DriveTest;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const driveTestFields = nameofFactory<Api.DriveTest>();

export type EditDriveTestProps = StateProps & DispatchProps & OwnProps;

export type EditDriveTestState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsDriveTest[];
  locsEdited: Api.LocsDriveTest[];
};

export class EditDriveTest extends React.Component<EditDriveTestProps, EditDriveTestState> {
  constructor(props: EditDriveTestProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedDriveTest,
      storedDriveTest,
      pdApi,
      activeUser,
      toggleModal,
      columns,
      isShortenTextEnabled
    } = this.props;
    const isNew = !storedDriveTest;
    const permissions = hasWriteAccess(activeUser, "Drive");
    return (
      <Editor
        itemName={"drive test"}
        originalObject={storedDriveTest}
        editedObject={editedDriveTest}
        hasWritePermission={hasWriteAccess(activeUser, "Drive")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedDriveTest,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("driveTests");
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedDriveTest,
            locs: this.state.locsEdited
          });
          this.props.editActions.add("driveTests");
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedDriveTest,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedDriveTest, true);
        }}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="code"
          label="Code"
          value={editedDriveTest.code}
          isError={!this.isFieldValid(driveTestFields("code"))}
          errorMessage="The code must be unique."
          readonly={!isNew}
          onChange={code => this.props.editActions.setEdited({ ...editedDriveTest, code })}
        />

        <TextField
          id="swName"
          label="Sw Name"
          value={editedDriveTest.swName}
          isError={!this.isFieldValid(driveTestFields("swName"))}
          errorMessage={
            !this.isEmptyString(editedDriveTest.swName) ? "The Sw name must be unique." : ""
          }
          onChange={swName =>
            this.props.editActions.setEdited({ ...editedDriveTest, swName: swName.trim() })
          }
          readonly={!permissions}
        />
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        <TextField
          id="englishName"
          label="English Name"
          value={editedDriveTest.name}
          isError={!this.isFieldValid(driveTestFields("name"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedDriveTest,
              name: value.trim()
            } as Api.DriveTest);
          }}
          readonly={!permissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedDriveTest.description}
          isError={!this.isFieldValid(driveTestFields("description"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedDriveTest,
              description: value.trim()
            } as Api.DriveTest);
          }}
          readonly={!permissions}
        />
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />

        <CheckboxField
          id={"localizable"}
          label={"Test is localizable"}
          checked={editedDriveTest.localizable}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedDriveTest, localizable: value })
          }
          disabled={!permissions}
        />
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Drive Test"}
            pdApi={pdApi}
            editedItem={{ ...editedDriveTest, locs: this.state.locsEdited }}
            getOption="driveTestLocalizations"
            columns={columns["Drive Test Localization Status"]}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={permissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedDriveTest.name !== this.props.storedDriveTest?.name ||
    this.props.editedDriveTest.description !== this.props.storedDriveTest?.description;

  private handleEnglishTranslationsChange(item: Api.DriveTest) {
    const { storedDriveTest, editedDriveTest } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              description: item.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveTest
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name,
                description: item.description
              } as Api.LocsDriveTest;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedDriveTest?.name,
              description: storedDriveTest?.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveTest
          ]
        : locsOriginal;

    this.setState((prevState: EditDriveTestState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedDriveTest,
      name: item.name,
      description: item.description
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditDriveTestState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsDriveTest[],
    oldLocalizations: Api.LocsDriveTest[]
  ): void => {
    const { editedDriveTest } = this.props;
    this.setState((prevState: EditDriveTestState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedDriveTest,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedDriveTest?.name,
      description:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.description ||
        editedDriveTest?.description,
      locs: newLocalizations
    } as Api.DriveTest);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsDriveTest[]): void => {
    this.setState((prevState: EditDriveTestState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  private isFieldValid = (field: string): boolean => {
    const { pdApi, editedDriveTest, storedDriveTest } = this.props;
    const isNew = !storedDriveTest;
    if (!Number.isInteger(editedDriveTest.code)) return false;
    switch (field) {
      case driveTestFields("code"):
        return _.values(pdApi.driveTests).find(
          driveTest =>
            (isNew || driveTest.id !== editedDriveTest.id) &&
            driveTest.code === editedDriveTest.code
        )
          ? false
          : true;
      case driveTestFields("swName"): {
        const { swName } = editedDriveTest;
        if (this.isEmptyString(swName)) {
          return false;
        } else {
          return _.values(pdApi.driveTests).find(
            driveTest =>
              (isNew || driveTest.id !== editedDriveTest.id) &&
              driveTest.swName?.trim().toLowerCase() ===
                editedDriveTest.swName?.trim().toLowerCase()
          )
            ? false
            : true;
        }
      }
      case driveTestFields("name"): {
        const { name } = editedDriveTest;
        return !this.isEmptyString(name);
      }
      case driveTestFields("description"): {
        const { description } = editedDriveTest;
        return !this.isEmptyString(description);
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedDriveTest } = this.props;
    const temp = _.keys(editedDriveTest);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedDriveTest: state.editView.editedObject,
  storedDriveTest: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDriveTest);


